import React, { useState, useEffect, memo } from 'react'

import './ChordProgressionModal.css'

// Types
import type { 
  ChordParseResult, 
  ParsedChord, 
  ExampleProgression 
} from '../utils/chordParser'

// Utilities
import { parseChordProgression, getExampleProgressions } from '../utils/chordParser'

/**
 * ChordProgressionModal component
 * Provides an interface for entering and parsing chord progressions
 * with real-time validation and example chord formats
 */

interface ChordProgressionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onGenerate: (chords: ParsedChord[]) => void;
}

export function ChordProgressionModal({ isOpen, onClose, onGenerate }: ChordProgressionModalProps) {
  const [input, setInput] = useState<string>('');
  const [parseResult, setParseResult] = useState<ChordParseResult>({ chords: [], errors: [] });
  const [selectedExample, setSelectedExample] = useState<string>('');

  // Parse chords in real-time as user types
  useEffect(() => {
    if (input.trim()) {
      const result = parseChordProgression(input);
      setParseResult(result);
    } else {
      setParseResult({ chords: [], errors: [] });
    }
  }, [input]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (parseResult.chords.length === 0) {
      return;
    }

    onGenerate(parseResult.chords);
    handleClose();
  };

  const handleClose = () => {
    setInput('');
    setParseResult({ chords: [], errors: [] });
    setSelectedExample('');
    onClose();
  };

  const handleExampleSelect = (example: ExampleProgression) => {
    setInput(example.chords);
    setSelectedExample(example.name);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      handleClose();
    } else if (e.key === 'Enter' && e.ctrlKey) {
      handleSubmit(e as React.FormEvent);
    }
  };

  if (!isOpen) return null;

  const examples = getExampleProgressions();
  const hasValidChords = parseResult.chords.length > 0;
  const hasErrors = parseResult.errors.length > 0;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content chord-progression-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>🎵 Enter Chord Progression</h2>
          <button className="modal-close-btn" onClick={handleClose}>×</button>
        </div>

        <div className="modal-body">
          {/* Input Section */}
          <div className="input-section">
            <label htmlFor="chord-input">Chord Progression:</label>
            <input
              id="chord-input"
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="e.g., C Am F G  or  Dm7 G7 Cmaj7  or  C | Am | F | G"
              className="chord-input"
              autoFocus
            />
            <div className="input-help">
              Separate chords with spaces, commas, or pipes (|). Press Ctrl+Enter to generate.
            </div>
          </div>

          {/* Live Preview Section */}
          {input.trim() && (
            <div className="preview-section">
              <h3>Preview:</h3>
              
              {hasValidChords && (
                <div className="valid-chords">
                  <div className="preview-label">
                    Will create {parseResult.chords.length} guitar{parseResult.chords.length !== 1 ? 's' : ''}:
                  </div>
                  <div className="chord-list">
                    {parseResult.chords.map((chord, index) => (
                      <span key={index} className="chord-preview">
                        {chord.root}{chord.quality === 'MAJOR_TRIAD' ? '' : chord.quality.toLowerCase()}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {hasErrors && (
                <div className="error-section">
                  <div className="error-label">Issues found:</div>
                  {parseResult.errors.map((error, index) => (
                    <div key={index} className="error-message">
                      ⚠️ {error}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Examples Section */}
          <div className="examples-section">
            <h3>Example Progressions:</h3>
            <div className="examples-grid">
              {examples.map((example, index) => (
                <button
                  key={index}
                  className={`example-btn ${selectedExample === example.name ? 'selected' : ''}`}
                  onClick={() => handleExampleSelect(example)}
                >
                  <div className="example-name">{example.name}</div>
                  <div className="example-chords">{example.chords}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Supported Formats Section */}
          <div className="formats-section">
            <details>
              <summary>Supported Chord Formats</summary>
              <div className="formats-content">
                <div className="format-group">
                  <strong>Basic:</strong> C, D, E, F, G, A, B
                </div>
                <div className="format-group">
                  <strong>Accidentals:</strong> C#, Db, F#, Bb
                </div>
                <div className="format-group">
                  <strong>Minor:</strong> Cm, Am, Dm (or c, am, dm)
                </div>
                <div className="format-group">
                  <strong>7th Chords:</strong> C7, Cmaj7, Cm7, Am7
                </div>
                <div className="format-group">
                  <strong>Extended:</strong> C6, Cm6, Csus2, Csus4, Cadd9
                </div>
                <div className="format-group">
                  <strong>Other:</strong> Cdim, Caug, C5 (power chord)
                </div>
              </div>
            </details>
          </div>
        </div>

        <div className="modal-footer">
          <button 
            type="button" 
            className="btn-secondary" 
            onClick={handleClose}
          >
            Cancel
          </button>
          <button 
            type="submit" 
            className="btn-primary" 
            onClick={handleSubmit}
            disabled={!hasValidChords}
          >
            Generate {parseResult.chords.length > 0 ? `${parseResult.chords.length} ` : ''}Guitar{parseResult.chords.length !== 1 ? 's' : ''}
          </button>
        </div>
      </div>
    </div>
  );
}

// Memoize ChordProgressionModal for performance optimization
export default memo(ChordProgressionModal);
